export class EventEmitter {
  constructor() {
    this._EventEmitterEvents = {};
    this._EventEmitterEventsComplete = {};
  }

  static extend(ChildClass) {
    // Mix EventEmitter methods into the child class prototype
    Object.getOwnPropertyNames(EventEmitter.prototype).forEach((method) => {
      if (method !== "constructor") {
        ChildClass.prototype[method] = EventEmitter.prototype[method];
      }
    });

    // Initialize EventEmitter properties
    ChildClass.prototype._EventEmitterEvents = {};
    ChildClass.prototype._EventEmitterEventsComplete = {};
  }

  on(key, fn) {
    // Add default namespace if not provided
    if (!key.includes(".")) {
      key = `${key}.default`;
    }

    const parentKey = key.split(".")[0];

    // Initialize event array if needed
    if (!this._EventEmitterEvents[key]) {
      this._EventEmitterEvents[key] = [];
    }

    const parents = Object.keys(this._EventEmitterEventsComplete).map((k) =>
      k ? k.split(".")[0] : ""
    );

    const keys = Object.keys(this._EventEmitterEventsComplete);

    if (parents.includes(parentKey)) {
      // Handle parent key matches
      keys.forEach((k) => {
        const parts = k.split(".");
        if (parts[0] === parentKey) {
          try {
            fn.apply(this, this._EventEmitterEventsComplete[k]);
          } catch (e) {
            console?.trace?.(e);
          }
        }
      });

      this.off(key, this._EventEmitterEventsComplete[parentKey]);
      if (!this._EventEmitterEvents[key]) {
        this._EventEmitterEvents[key] = [];
      }
      this._EventEmitterEvents[key].push(fn);
    } else if (keys.includes(key)) {
      // Handle exact key match
      try {
        fn.apply(this, this._EventEmitterEventsComplete[key]);
      } catch (e) {
        console?.trace?.(e);
      }

      this.off(key, this._EventEmitterEventsComplete[key]);
      if (!this._EventEmitterEvents[key]) {
        this._EventEmitterEvents[key] = [];
      }
      this._EventEmitterEvents[key].push(fn);
    } else {
      // Simple case - just add the event handler
      this._EventEmitterEvents[key].push(fn);
    }

    return this;
  }

  off(key, fn) {
    // Clean up all events if no key provided
    if (!key) {
      Object.keys(this._EventEmitterEvents).forEach((eventKey) => {
        this._EventEmitterEvents[eventKey] = null;
        delete this._EventEmitterEvents[eventKey];
        this._EventEmitterEventsComplete[eventKey] = null;
        delete this._EventEmitterEventsComplete[eventKey];
      });
      return;
    }

    // Add default namespace if not provided
    if (!key.includes(".")) {
      key = `${key}.default`;
    }

    // Remove all handlers for this key if no specific function provided
    if (fn === undefined) {
      this._EventEmitterEvents[key] = null;
      delete this._EventEmitterEvents[key];
      this._EventEmitterEventsComplete[key] = null;
      delete this._EventEmitterEventsComplete[key];
      return;
    }

    const fnStr = String(fn);

    // Helper to remove a specific event handler
    const removeEvent = (store, key, fnStr) => {
      if (!store[key]) return;

      for (let i = 0; i < store[key].length; i++) {
        const evtFnStr = String(store[key][i]);
        if (fnStr === evtFnStr || evtFnStr === "[object Object]") {
          store[key].splice(i, 1);
          if (store[key].length === 0) {
            delete store[key];
            break;
          }
        }
      }
    };

    removeEvent(this._EventEmitterEvents, key, fnStr);
    removeEvent(this._EventEmitterEventsComplete, key, fnStr);

    return this;
  }

  trigger(key, ...args) {
    let fired;
    const fullArgs = [key, ...args];

    // Fire event for matching namespaces
    const fireEvent = (namespace, eventArgs) => {
      if (!this._EventEmitterEvents[namespace]) {
        return;
      }

      const handlers = this._EventEmitterEvents[namespace];
      this._EventEmitterEventsComplete[namespace] = eventArgs;

      // Remove the event name from arguments
      const handlerArgs = eventArgs.slice(1);

      handlers.forEach((fn) => {
        try {
          fn.apply(this, handlerArgs);
        } catch (e) {
          console?.trace?.(e);
        }
      });

      return true;
    };

    // Check for matching namespaces
    Object.keys(this._EventEmitterEvents).forEach((namespace) => {
      const re = new RegExp(`^${key}\\.`);
      if (re.test(namespace)) {
        fired = fireEvent(namespace, [...fullArgs]);
      }
    });

    // Store events that haven't been registered yet
    if (!fired) {
      const finalKey = key.includes(".") ? key : `${key}.default`;
      this._EventEmitterEventsComplete[finalKey] = args;
    }
  }
}

// For AMD compatibility
if (typeof define === "function" && define.amd) {
  define([], () => EventEmitter);
} else if (typeof window !== "undefined") {
  window.EventEmitter = EventEmitter;
}
